import React from 'react';
import { FaProjectDiagram, FaUserTie, FaShieldAlt, FaCertificate, FaMoneyBillWave, FaClock } from 'react-icons/fa';

const WhyChooseUsSection = () => {
    const features = [
        {
            icon: <FaProjectDiagram />,
            title: 'End-to-End Development',
            description: 'From wireframes to deployment, we handle the entire product lifecycle so you can focus on growing your business.',
            highlight: 'Full-stack expertise',
            delay: '0'
        },
        {
            icon: <FaUserTie />,
            title: 'Dedicated Engineering Teams',
            description: 'Work with a dedicated team of engineers, designers, and product managers who act as an extension of your own company.',
            highlight: 'Seamless integration',
            delay: '100'
        },
        {
            icon: <FaShieldAlt />,
            title: 'Govt. Registered & Licensed',
            description: 'We hold valid registrations under Central Government and are a recognized commercial establishment by the State Government. Fully legal, fully transparent.',
            highlight: 'Central & State verified',
            delay: '200'
        },
        {
            icon: <FaCertificate />,
            title: 'Quality & Code Standards',
            description: 'We follow industry best practices, write clean, scalable code, and implement rigorous QA testing before any release.',
            highlight: 'Scalable architecture',
            delay: '300'
        },
        {
            icon: <FaMoneyBillWave />,
            title: 'Transparent Pricing',
            description: 'No hidden fees or surprise invoices. We provide detailed estimates and deliver high-ROI solutions within your budget.',
            highlight: 'Fixed cost & T&M models',
            delay: '400'
        },
        {
            icon: <FaClock />,
            title: 'Agile & On-Time Delivery',
            description: 'Our agile sprint methodology ensures rapid iterations, regular updates, and strict adherence to project deadlines.',
            highlight: 'Predictable timelines',
            delay: '500'
        },
    ];

    return (
        <>
            <section className="ob-diff-section">
                <div className="ob-diff-glow-1"></div>
                <div className="ob-diff-glow-2"></div>

                <div className="container ob-diff-inner">
                    <div className="text-center mb-5" data-aos="fade-up">
                        <span className="ob-diff-badge">WHY OCTALBEES</span>
                        <h2 className="ob-diff-heading">
                            The <span className="ob-diff-gradient">Octalbees</span> Difference
                        </h2>
                        <p className="ob-diff-subheading mx-auto">
                            We're not just another agency or training company. Here's what actually sets us apart.
                        </p>
                    </div>

                    <div className="row g-4">
                        {features.map((feature, index) => (
                            <div className="col-lg-4 col-md-6" key={index} data-aos="fade-up" data-aos-delay={feature.delay}>
                                <div className="ob-diff-card">
                                    <div className="ob-diff-card-accent"></div>
                                    <div className="ob-diff-icon-wrap">
                                        <span className="ob-diff-icon">{feature.icon}</span>
                                    </div>
                                    <h4 className="ob-diff-card-title">{feature.title}</h4>
                                    <p className="ob-diff-card-desc">{feature.description}</p>
                                    <div className="ob-diff-highlight">
                                        <span className="ob-diff-highlight-dot"></span>
                                        {feature.highlight}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            
            <style>{`
                /* =============================================
                   THE OCTALBEES DIFFERENCE — REDESIGNED
                   ============================================= */

                .ob-diff-section {
                    padding: 110px 0;
                    background-color: #0c001f;
                    position: relative;
                    overflow: hidden;
                    color: #fff;
                }

                .ob-diff-inner { position: relative; z-index: 2; }

                .ob-diff-glow-1 {
                    position: absolute; width: 500px; height: 500px;
                    background: radial-gradient(circle, rgba(160, 79, 244, 0.1) 0%, transparent 70%);
                    border-radius: 50%; top: -10%; left: -8%; pointer-events: none;
                }

                .ob-diff-glow-2 {
                    position: absolute; width: 450px; height: 450px;
                    background: radial-gradient(circle, rgba(255, 180, 40, 0.07) 0%, transparent 70%);
                    border-radius: 50%; bottom: -12%; right: -8%; pointer-events: none;
                }

                /* Header */
                .ob-diff-badge {
                    display: inline-block; font-size: 0.75rem; font-weight: 700;
                    letter-spacing: 3px; color: #ffb428;
                    background: rgba(255, 180, 40, 0.1); padding: 7px 20px;
                    border-radius: 50px; border: 1px solid rgba(255, 180, 40, 0.2);
                    margin-bottom: 20px; text-transform: uppercase;
                }

                .ob-diff-heading {
                    font-size: 3rem; font-weight: 800; color: #fff;
                    letter-spacing: -1px; margin-bottom: 1rem;
                }

                .ob-diff-gradient {
                    color: #a04ff4;
                    text-shadow: 0 0 30px rgba(160, 79, 244, 0.3);
                }

                .ob-diff-subheading {
                    font-size: 1.1rem; color: #8a8a8a; max-width: 600px;
                    line-height: 1.7; text-align: center;
                }

                /* ─── Cards ─── */
                .ob-diff-card {
                    background: rgba(255, 255, 255, 0.02);
                    border: 1px solid rgba(255, 255, 255, 0.05);
                    border-radius: 20px;
                    padding: 36px 28px 28px;
                    height: 100%;
                    position: relative;
                    overflow: hidden;
                    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                    backdrop-filter: blur(6px);
                    display: flex;
                    flex-direction: column;
                }

                .ob-diff-card-accent {
                    position: absolute; top: 0; left: 0; right: 0; height: 3px;
                    background: linear-gradient(90deg, #a04ff4, transparent);
                    opacity: 0; transition: opacity 0.4s ease;
                }

                .ob-diff-card:hover {
                    transform: translateY(-8px);
                    border-color: rgba(160, 79, 244, 0.25);
                    background: rgba(255, 255, 255, 0.04);
                    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4), 0 0 20px rgba(160, 79, 244, 0.06);
                }

                .ob-diff-card:hover .ob-diff-card-accent { opacity: 1; }

                /* Icon */
                .ob-diff-icon-wrap {
                    width: 54px; height: 54px;
                    background: rgba(160, 79, 244, 0.08);
                    border: 1px solid rgba(160, 79, 244, 0.15);
                    border-radius: 14px;
                    display: flex; align-items: center; justify-content: center;
                    margin-bottom: 22px;
                    transition: all 0.3s ease;
                }

                .ob-diff-icon {
                    font-size: 1.4rem; color: #a04ff4;
                    transition: color 0.3s ease;
                }

                .ob-diff-card:hover .ob-diff-icon-wrap {
                    background: #a04ff4;
                    border-color: #a04ff4;
                    box-shadow: 0 8px 20px rgba(160, 79, 244, 0.3);
                    transform: scale(1.05);
                }

                .ob-diff-card:hover .ob-diff-icon { color: #fff; }

                /* Text */
                .ob-diff-card-title {
                    font-size: 1.25rem; font-weight: 700; color: #fff;
                    margin-bottom: 12px;
                }

                .ob-diff-card-desc {
                    font-size: 0.95rem; color: #8a8a8a; line-height: 1.7;
                    margin: 0 0 auto 0; text-align: left;
                    flex-grow: 1;
                }

                /* Highlight badge at bottom */
                .ob-diff-highlight {
                    display: inline-flex; align-items: center; gap: 8px;
                    margin-top: 20px; padding-top: 16px;
                    border-top: 1px solid rgba(255, 255, 255, 0.04);
                    font-size: 0.82rem; font-weight: 600; color: #ffb428;
                    letter-spacing: 0.3px; text-transform: uppercase;
                }

                .ob-diff-highlight-dot {
                    width: 6px; height: 6px; border-radius: 50%;
                    background: #ffb428; flex-shrink: 0;
                    box-shadow: 0 0 8px rgba(255, 180, 40, 0.4);
                    animation: dotPulse 2s ease-in-out infinite;
                }

                @keyframes dotPulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.5; transform: scale(0.7); }
                }

                /* ─── Responsive ─── */
                @media (max-width: 991px) {
                    .ob-diff-heading { font-size: 2.5rem; }
                    .ob-diff-section { padding: 80px 0; }
                }

                @media (max-width: 768px) {
                    .ob-diff-section { padding: 70px 15px; }
                    .ob-diff-heading { font-size: 2.2rem; }
                    .ob-diff-card { padding: 28px 22px 22px; }
                }
            `}</style>
        </>
    );
};

export default WhyChooseUsSection;